import React, { useState } from "react";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { TERMS_VERSION, TermsContent } from "./Terms";
import Icon from "./Icon";
import { C, R, S } from "../theme";

// Blocking gate: owners must (re)accept the current Terms & Conditions version
// before continuing. Fires on first login after a version bump, and for legacy
// owners who never had a stamped acceptance.
export default function TermsGate() {
  const { user, acceptTerms } = useAuth();
  const { t: tr } = useI18n();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user || user.role !== "owner") return null;
  if (user.terms_accepted_version === TERMS_VERSION) return null;

  const isUpdate = !!user.terms_accepted_version;

  const onAccept = async () => {
    if (!agreed) return;
    setErr(""); setBusy(true);
    try { await acceptTerms(); }
    catch (e) { setErr(e?.message || tr("terms.couldNotRecord")); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop modal-center" style={{ zIndex: 10000 }}>
      <div data-testid="terms-gate" style={{ background: C.card, borderRadius: R.lg, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", border: `1px solid ${C.borderStrong}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: `${S.lg}px ${S.lg}px ${S.md}px`, borderBottom: `1px solid ${C.border}` }}>
          <Icon name="shield-checkmark" size={24} color={C.amber} />
          <div className="display" style={{ color: C.text, fontSize: 18, fontWeight: 900 }}>{isUpdate ? tr("terms.updatedTitle") : tr("terms.acceptTitle")}</div>
        </div>
        <div style={{ padding: `${S.md}px ${S.lg}px 0` }}>
          <div style={{ background: "rgba(245,158,11,0.12)", border: `1px solid ${C.amber}`, borderRadius: R.md, padding: S.md, color: C.text, fontSize: 13, lineHeight: 1.5 }}>
            {isUpdate
              ? `${tr("terms.gateUpdatePre")} ${TERMS_VERSION}${tr("terms.gateUpdatePost")}`
              : tr("terms.gateNew")}
          </div>
        </div>
        <div style={{ padding: `${S.md}px ${S.lg}px`, overflowY: "auto" }}>
          <TermsContent />
        </div>
        <div style={{ padding: S.lg, borderTop: `1px solid ${C.border}` }}>
          <button type="button" data-testid="terms-gate-checkbox" onClick={() => setAgreed((v) => !v)} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "transparent", border: "none", padding: 0, textAlign: "left", marginBottom: S.md, cursor: "pointer", width: "100%" }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, border: `2px solid ${agreed ? C.green : C.borderStrong}`, background: agreed ? C.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
              {agreed && <Icon name="checkmark" size={16} color={C.black} />}
            </span>
            <span style={{ color: C.sub, fontSize: 14, lineHeight: 1.5 }}>{tr("terms.agreeCheckbox")}</span>
          </button>
          {!!err && <div style={{ color: C.red, fontWeight: 700, fontSize: 13, marginBottom: S.sm }}>{err}</div>}
          <button data-testid="terms-gate-accept" className="pressable" disabled={!agreed || busy} onClick={onAccept} style={{ width: "100%", background: agreed ? C.amber : C.card2, color: agreed ? C.black : C.muted, fontWeight: 900, fontSize: 16, border: "none", borderRadius: R.pill, minHeight: 52, opacity: busy ? 0.7 : 1 }}>
            {busy ? tr("terms.recording") : `${tr("terms.acceptContinue")} (v${TERMS_VERSION})`}
          </button>
        </div>
      </div>
    </div>
  );
}
